import i18n from 'i18next'

import {forwardCancellation} from 'store/promise'
import type {HTTPRequest, HTTPResponse} from 'store/http'
import {cleanHtmlError, hasErrorStatus} from 'store/http'

async function handleJsonResponse<T>(response: HTTPResponse): Promise<T> {
  // Errors are in HTML, not JSON.
  if (hasErrorStatus(response)) {
    throw await cleanHtmlError(response)
  }
  return response.json()
}

const fetchWithoutCookies = (path: string, request: HTTPRequest, isCancelable = !!AbortController):
Promise<HTTPResponse> => {
  const controller = isCancelable ? new AbortController() : {abort: () => void 0, signal: undefined}
  return forwardCancellation(
    fetch(path, {
      // TODO(pascal): Re-enable cookies from the client but probably not on all URLs. They were
      // removed in da1a8acd089d97163b54bfe70b87e348da484205 but we are now going to use them.
      credentials: 'omit',
      ...request,
      headers: {
        ...request.headers,
        ...(i18n.languages ? {'Accept-language': i18n.languages.join(',')} : undefined),
      },
      signal: controller.signal,
    }),
    () => controller.abort(),
  )
}


interface PostOptions {
  isExpectingResponse?: boolean
  isCancelable?: boolean
}


function postJson<T>(
  path: string, data: unknown,
  options: {isExpectingResponse: true} & PostOptions): Promise<T>
function postJson(
  path: string, data: unknown,
  options?: {isExpectingResponse?: false} & PostOptions): Promise<HTTPResponse>
async function postJson<T>(
  path: string, data: unknown, options?: PostOptions): Promise<T | HTTPResponse> {
  const {isExpectingResponse, isCancelable = !!AbortController} = options || {}
  const headers: {[key: string]: string} = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
  const response = await fetchWithoutCookies(path, {
    body: JSON.stringify(data),
    headers,
    method: 'post',
  }, isCancelable)
  if (isExpectingResponse) {
    return handleJsonResponse<T>(response)
  }
  return response
}

async function getJson<T>(path: string): Promise<T> {
  const headers: {[key: string]: string} = {
    Accept: 'application/json',
  }
  const response = await fetchWithoutCookies(path, {headers})
  return handleJsonResponse<T>(response)
}

function feedbackPost(feedback: bayes.bob.Feedback): Promise<unknown> {
  return postJson('/api/feedback', feedback)
}

function projectLaborStatsPost(user: bayes.bob.User): Promise<bayes.bob.LaborStatsData> {
  return postJson('/api/compute-labor-stats', user, {isExpectingResponse: true})
}

async function upskillingSectionsPost(user: bayes.bob.User):
Promise<readonly bayes.upskilling.Section[]> {
  const {sections} = await postJson<bayes.upskilling.Sections>(
    '/api/upskilling/sections', user, {isExpectingResponse: true})
  return sections || []
}

async function upskillingSectionsMoreJobPost(
  user: bayes.bob.User, section: bayes.upskilling.Section,
): Promise<readonly bayes.upskilling.Job[]> {
  const {jobs} = await postJson<bayes.upskilling.Section>(
    `/api/upskilling/sections/${section.id || ''}/jobs/${section.state || ''}`, user,
    {isExpectingResponse: true})
  return jobs || []
}

function upskillingSaveUser(user: bayes.bob.User): Promise<unknown> {
  return postJson('/api/upskilling/user', user)
}

function upskillingFeedbackVolunteeringSend(email: string): Promise<unknown|string> {
  return fetch(`/api/feedback/volunteer/Jobflix?email=${email}`)
}

function upskillingJobsGet(): Promise<bayes.bob.JobGroupList> {
  return getJson('/api/upskilling/jobs')
}

export {
  feedbackPost,
  getJson,
  postJson,
  projectLaborStatsPost,
  upskillingFeedbackVolunteeringSend,
  upskillingJobsGet,
  upskillingSaveUser,
  upskillingSectionsMoreJobPost,
  upskillingSectionsPost,
}
